import { computed, getCurrentScope, onScopeDispose, ref, watch, type ComputedRef } from 'vue'

import { insightActionsApi } from '../api/InsightActionsApi'
import { ApprovalRunFlow } from '../flows/ApprovalRunFlow'
import type { ApprovalRunOrigin } from '../models/insightAction.types'
import {
  groupApprovalRows,
  type ApprovalQueueEntry,
  type ApprovalRunGroup,
} from '../service/approvalRunGroups'

import { useInsightActionScope } from './useInsightActionScope'

import type { ApprovalInboxRow } from '@/features/platform-actions'

export interface UseApprovalOrigins {
  entries: ComputedRef<ApprovalQueueEntry[]>
  groups: ComputedRef<ApprovalRunGroup[]>
  loose: ComputedRef<ApprovalInboxRow[]>
  originOf: (requestId: string) => ApprovalRunOrigin | null
  refresh: () => Promise<void>
}

/** Internal injection seam: production callers use the one-argument contract. */
export interface UseApprovalOriginsOptions {
  flow?: Pick<ApprovalRunFlow, 'lookup'>
}

/**
 * Which insight run each request on the approvals page came from — one bulk lookup for the whole
 * page, re-asked only when the set of requests on it changes (the inbox's silent 30 s re-read
 * hands back the same ids and costs nothing here).
 */
export function useApprovalOrigins(
  rows: ComputedRef<ApprovalInboxRow[]>,
  options: UseApprovalOriginsOptions = {},
): UseApprovalOrigins {
  const scope = useInsightActionScope()
  const flow = options.flow ?? new ApprovalRunFlow({ api: insightActionsApi })
  const origins = ref<Record<string, ApprovalRunOrigin | null>>({})
  let controller: AbortController | null = null

  const ids = computed(() => rows.value.map((row) => row.request.id).filter(Boolean))

  async function load(): Promise<void> {
    controller?.abort()
    const runScope = scope.forRun()
    const requestIds = ids.value
    if (!runScope || requestIds.length === 0) {
      origins.value = {}
      return
    }
    const current = new AbortController()
    controller = current
    const next = await flow.lookup(runScope, requestIds, current.signal)
    // A newer page claimed the lookup while this one was in flight.
    if (controller !== current) return
    origins.value = next
  }

  watch(
    () => [ids.value.join('|'), scope.accountId.value, scope.workspaceId.value] as const,
    () => {
      void load()
    },
    { immediate: true },
  )

  if (getCurrentScope()) onScopeDispose(() => controller?.abort())

  const queue = computed(() => groupApprovalRows(rows.value, origins.value))

  return {
    entries: computed(() => queue.value.entries),
    groups: computed(() => queue.value.groups),
    loose: computed(() => queue.value.loose),
    originOf: (requestId) => origins.value[requestId] ?? null,
    refresh: load,
  }
}
